import {
	InputLabel,
	InputLabelProps,
	PopoverActions,
	Select,
	SelectChangeEvent,
	SelectProps,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { combineSX } from '@/utils/combineSX';
import { DpOutlinedSelectSX } from '@/components/blocks/DpOutlineSelect/styles/main';
import { ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react';

interface IconComponentProps {
	disabled?: boolean;
}

export const IconComponent = ({ disabled }: IconComponentProps) => (
	<KeyboardArrowDownIcon
		sx={{
			color: disabled ? '#9e9e9e' : '#774A91',
			position: 'absolute',
			right: '7px',
			top: 'calc(50% - .5em)',
			pointerEvents: 'none',
		}}
	/>
);

export const DpOutlinedSelect: React.FC<
	SelectProps & { onChange?: (event: SelectChangeEvent<unknown>, child: ReactNode) => void }
> = ({ disabled, sx, MenuProps, onOpen, onClose, ...props }) => {
	const popoverActionRef = useRef<PopoverActions>(null);
	const [open, setOpen] = useState(false);

	// Mientras el menú está abierto, lo reposicionamos en cada scroll para que
	// permanezca anclado al select (incluye scroll de contenedores anidados, por eso
	// se escucha en captura).
	useEffect(() => {
		if (!open) {
			return;
		}
		const handleScroll = () => popoverActionRef.current?.updatePosition();
		window.addEventListener('scroll', handleScroll, true);
		return () => window.removeEventListener('scroll', handleScroll, true);
	}, [open]);

	return (
		<Select
			IconComponent={() => <IconComponent disabled={disabled} />}
			disabled={disabled}
			sx={combineSX([DpOutlinedSelectSX, ...(Array.isArray(sx) ? sx : [sx])])}
			onOpen={(event: SyntheticEvent) => {
				setOpen(true);
				onOpen?.(event);
			}}
			onClose={(event: SyntheticEvent) => {
				setOpen(false);
				onClose?.(event);
			}}
			MenuProps={{
				...MenuProps,
				action: popoverActionRef,
			}}
			{...props}
		/>
	);
};

export const DpOutlinedSelectLabel: React.FC<InputLabelProps> = ({ children, ...props }) => (
	<InputLabel
		sx={combineSX([
			{
				backgroundColor: '#FFF',
				fontSize: 'var(--font-size-body)',
				lineHeight: '1',
				height: '20px',
				'& .MuiFormLabel-asterisk': {
					display: 'none',
				},
			},
			...(Array.isArray(props.sx) ? props.sx : [props.sx]),
		])}
		{...props}
	>
		{children}
	</InputLabel>
);
